/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
} from '@gh/builder-shared';
import { ComponentItemFilterMode } from '@gh/builder-shared-data';
import {
  FormState,
  FormStateBuilder,
  getRouteInitData,
  getSingleNewSelectedEntity,
} from '@gh/common-shared';
import { emptySelectionModel, SelectionModel, SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  setControlValue,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import {
  BulkUpdateHousePlanReplace,
  getBulkUpdateItemComponentModelFromFinder,
} from '../../resources/bulk-update.model';
import { HousePlanBulkUpdate } from '../../resources/house-plan-bulk-update.model';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';
import { HousePlanGridDefinition } from '../house-plan-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-bulk-update-hp-replace',
  templateUrl: 'bulk-update-hp-replace.component.html',
  styleUrls: ['bulk-update-hp-replace.component.scss'],
  providers: [HousePlanGridDefinition],
})
export class BulkUpdateHpReplaceComponent extends BulkUpdateAbstractComponent implements OnInit {

  housePlanDataSource: SlicedDataSource<HousePlanBulkUpdate>;
  form: FormState<any>;
  targetSelectionModel = emptySelectionModel();
  dirty: boolean = false;

  constructor(public housePlanGridDefinition: HousePlanGridDefinition,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private route: ActivatedRoute,
              private waitingService: WaitingService,
              private businessService: BulkUpdateBusinessService,
              private formStateBuilder: FormStateBuilder) {
    super();
  }

  get componentItemFrom(): ComponentItemFinderListItem {
    return this.form.group.get('from')!.value;
  }

  get componentItemTo(): ComponentItemFinderListItem {
    return this.form.group.get('to')!.value;
  }

  get allowUpdate(): boolean {
    return this.form.group.get('allowUpdate')!.value;
  }

  ngOnInit(): void {
    const { initData } = getRouteInitData(this.route);
    this.form = this.formStateBuilder.buildPlain({
      children: {
        from: void 0,
        to: void 0,
        allowUpdate: { enabled: initData.allowUpdate },
      },
    });
    this.form.valueChanges.subscribe(() => this.resetDirty(true));
    this.load();
  }

  load(): void {
    this.housePlanDataSource = this.businessService.loadHousePlansGridDataSource();
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.targetSelectionModel = model;
  }

  onSave(): void {
    this.saveAction();
  }

  onSearchFromElement(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      multiselection: false,
      disableFilterMode: false,
      preselected: [],
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((componentItem: ComponentItemFinderListItem) =>
        setControlValue(this.form.group, 'from', componentItem));
  }

  onSearchToElement(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      multiselection: false,
      filterMode: this.componentItemFrom.component ? ComponentItemFilterMode.Components : ComponentItemFilterMode.Items,
      disableFilterMode: true,
      preselected: [],
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((componentItem: ComponentItemFinderListItem) =>
        setControlValue(this.form.group, 'to', componentItem));
  }

  protected afterSave(): void {
    this.notificationService.success('msg_bulk_update_initiated_successfully');
    this.form.group.reset();
    this.targetSelectionModel = emptySelectionModel();
    this.resetDirty(false);
  }

  protected getRequestBody(): BulkUpdateHousePlanReplace {
    return {
      targetHousePlans: this.targetSelectionModel,
      oldEntity: getBulkUpdateItemComponentModelFromFinder(this.componentItemFrom),
      newEntity: getBulkUpdateItemComponentModelFromFinder(this.componentItemTo),
      allowUpdate: this.allowUpdate,
    };
  }

  protected saveAction(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.confirmation().pipe(
      switchMap$(() =>
        this.businessService.replaceHpSave(this.getRequestBody()).pipe(
          this.waitingService.operator<any>())))
      .subscribe(
        () => this.afterSave(),
        this.notificationService.processAll,
      );
  }

  private confirmation(): Observable<boolean> {
    if ((this.componentItemFrom.uomId !== this.componentItemTo.uomId) && this.allowUpdate) {
      return this.dialogService.confirmation(yesNoConfirmation('msg_bu_replace_not_same_uom_allow_update')).pipe(
        filter$(yesAnswer),
        map$(Boolean));
    } else {
      return of$(true);
    }
  }

}
